import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BellFill, CheckCircleFill, ExclamationTriangleFill, InfoCircleFill, ArrowClockwise } from 'react-bootstrap-icons';
import { useNotifications } from '../contexts/NotificationContext';
import { NotificationItem } from '../api/notifications';
import { normalizeNotificationRoute } from '../utils/routes';
import './NotificationCenter.css';

function relativeTime(iso: string | null): string
{
    if (!iso) return '';
    const date = new Date(iso);
    const diffSec = Math.floor((Date.now() - date.getTime()) / 1000);
    if (Number.isNaN(diffSec)) return '';
    if (diffSec < 60) return 'just now';
    if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
    if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
    if (diffSec < 604800) return `${Math.floor(diffSec / 86400)}d ago`;
    return date.toLocaleDateString();
}

function statusIcon(status: NotificationItem['status']): React.ReactElement
{
    if (status === 'success')
    {
        return <CheckCircleFill className="notif-icon notif-icon-success" />;
    }
    if (status === 'failure')
    {
        return <ExclamationTriangleFill className="notif-icon notif-icon-failure" />;
    }
    return <InfoCircleFill className="notif-icon notif-icon-info" />;
}

function NotificationBell(): React.ReactElement
{
    const { notifications, unreadCount, refresh, markRead, markAllRead, loading } = useNotifications();
    const [open, setOpen] = useState<boolean>(false);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const navigate = useNavigate();

    useEffect(() => {
        function handleClickOutside(event: MouseEvent): void
        {
            if (!containerRef.current) return;
            if (!containerRef.current.contains(event.target as Node))
            {
                setOpen(false);
            }
        }
        if (open)
        {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
        return undefined;
    }, [open]);

    function handleToggle(): void
    {
        setOpen((prev) => {
            const next = !prev;
            if (next)
            {
                // Refresh on open so the panel is fresh.
                refresh();
            }
            return next;
        });
    }

    async function handleItemClick(item: NotificationItem): Promise<void>
    {
        if (!item.read_at)
        {
            await markRead(item.id);
        }
        setOpen(false);
        if (item.link_path)
        {
            navigate(normalizeNotificationRoute(item.link_path, {
                jobType: item.job_type,
                status: item.status,
                projectId: item.metadata?.project_id as string | number | null | undefined,
                jobId: item.metadata?.job_id as string | number | null | undefined,
            }) || item.link_path);
        }
    }

    return (
        <div className="notif-bell-wrapper" ref={containerRef}>
            <button
                className="topbar-bell-btn"
                type="button"
                title="Notifications"
                aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
                onClick={handleToggle}
            >
                <BellFill size={18} />
                {unreadCount > 0 && <span className="topbar-bell-dot" />}
            </button>
            {open && (
                <div className="notif-panel" role="dialog" aria-label="Notifications">
                    <div className="notif-panel-header">
                        <span className="notif-panel-title">Notifications</span>
                        <div className="notif-panel-actions">
                            <button
                                type="button"
                                className="notif-panel-action-btn"
                                onClick={() => refresh()}
                                title="Refresh"
                                disabled={loading}
                            >
                                <ArrowClockwise size={14} />
                            </button>
                            {unreadCount > 0 && (
                                <button
                                    type="button"
                                    className="notif-panel-action-btn"
                                    onClick={() => markAllRead()}
                                >
                                    Mark all read
                                </button>
                            )}
                        </div>
                    </div>
                    <div className="notif-panel-body">
                        {notifications.length === 0 ? (
                            <div className="notif-panel-empty">No notifications yet</div>
                        ) : (
                            notifications.map((item) => (
                                <button
                                    key={item.id}
                                    type="button"
                                    className={`notif-item ${item.read_at ? '' : 'notif-item-unread'}`}
                                    onClick={() => handleItemClick(item)}
                                >
                                    <span className="notif-item-icon">{statusIcon(item.status)}</span>
                                    <span className="notif-item-body">
                                        <span className="notif-item-title">{item.title}</span>
                                        {item.summary && (
                                            <span className="notif-item-summary">{item.summary}</span>
                                        )}
                                        <span className="notif-item-meta">
                                            {relativeTime(item.created_at)}
                                            {item.job_type ? ` · ${item.job_type.replace(/_/g, ' ')}` : ''}
                                        </span>
                                    </span>
                                    {!item.read_at && <span className="notif-item-dot" aria-hidden />}
                                </button>
                            ))
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

export default NotificationBell;
